import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

var cards_swipers = [];

const BASE_BREAKPOINTS = {
    0:    { slidesPerView: 1.2, spaceBetween: 14 },
    560:  { slidesPerView: 2, spaceBetween: 15 },
    640: { slidesPerView: 2,    spaceBetween: 16 },
    768:  { slidesPerView: 3, spaceBetween: 18 },
    900: { slidesPerView: 3,    spaceBetween: 18 },
    1024: { slidesPerView: 3,    spaceBetween: 20 },
    1200: { slidesPerView: 3,    spaceBetween: 24 },
};

// Finds the active breakpoint config for a given viewport width, matching
// Swiper's own "largest key <= current width" resolution behaviour.
function getActiveBreakpointConfig(width) {
    const keys = Object.keys(BASE_BREAKPOINTS).map(Number).sort((a, b) => a - b);
    let active = BASE_BREAKPOINTS[keys[0]];
    for (const key of keys) {
        if (width >= key) active = BASE_BREAKPOINTS[key];
    }
    return active;
}

// Pagination should only show when real slides outnumber the current
// breakpoint's slidesPerView (e.g. more than 3 at 768px+, since that's
// the slidesPerView from 768px upward). Uses a CSS class with !important
// so it can't be silently overridden by Swiper's own pagination CSS.
function updatePaginationVisibility(paginationEl, cardsEl, realSlideCount, forceShowPagination, forceHidePagination) {
    if (!paginationEl) return;

    if (forceHidePagination) {
        paginationEl.classList.add('pagination-force-hidden');
        cardsEl?.classList.remove('has-pagination');
        cardsEl?.classList.add('no-pagination');
        return;
    }

    const { slidesPerView } = getActiveBreakpointConfig(window.innerWidth);
    const shouldShow = forceShowPagination || realSlideCount > slidesPerView;

    paginationEl.classList.toggle('pagination-force-hidden', !shouldShow);
    cardsEl?.classList.toggle('has-pagination', shouldShow);
    cardsEl?.classList.toggle('no-pagination', !shouldShow);
}

// Autoplay rule: below 768px, always autoplay (if the section opts in).
// At 768px and above, only autoplay if there are more real slides than
// the current breakpoint's slidesPerView (i.e. there's actually more to see).
function shouldAutoplay(width, realSlideCount) {
    if (width < 768) return true;
    const { slidesPerView } = getActiveBreakpointConfig(width);
    return realSlideCount > slidesPerView;
}

function initialiseSwipers()
{
    cards_swipers.forEach(cards_swiper =>
    {
        if (cards_swiper instanceof Swiper)
        {
            cards_swiper.destroy();
        }
    });

    document.querySelectorAll('section.cards .cards_cards').forEach((cards) => {
        const parent_section = cards.closest("section");
        const swiperEl = cards.querySelector(".swiper");
        const wrapper = swiperEl?.querySelector(".swiper-wrapper");
        const realSlideCount = wrapper
            ? wrapper.querySelectorAll(".swiper-slide").length
            : 0;

        if (!swiperEl || realSlideCount === 0) return;

        const autoplayEnabled = parent_section.classList.contains("autoplay");

        // CMS-driven overrides. "show_*" forces the control on regardless of
        // overflow; "hide_*" forces it off regardless of overflow/show.
        const forceShowArrows = parent_section.classList.contains("show_navigation_arrows_outside");
        const forceHideArrows = parent_section.classList.contains("hide_navigation_arrows");
        const forceShowPagination = parent_section.classList.contains("show_pagination_dots");
        const forceHidePagination = parent_section.classList.contains("hide_pagination_dots");

        if (forceHideArrows && !forceShowArrows) {
            cards.querySelector(".swiper-button-prev")?.remove();
            cards.querySelector(".swiper-button-next")?.remove();
        }

        const paginationEl = cards.querySelector('.swiper-pagination');

        const swiper_options = {
            modules: [Navigation, Pagination, Autoplay],
            direction: 'horizontal',
            loop: false,
            // Always construct with autoplay config if the section opts in,
            // so the autoplay module exists and can be start()/stop() toggled.
            // Actual on/off state is resolved by syncAutoplay() below.
            autoplay: autoplayEnabled ? { delay: 3500, disableOnInteraction: false } : false,
            speed: 1000,
            spaceBetween: 15,
            slidesPerView: 'auto',
            centeredSlides: false,
            navigation: {
                nextEl: parent_section.querySelector(".swiper-button-next"),
                prevEl: parent_section.querySelector(".swiper-button-prev"),
            },
            pagination: {
                el: paginationEl,
                clickable: true,
            },
            breakpoints: BASE_BREAKPOINTS,
        };

        const swiperInstance = new Swiper(swiperEl, swiper_options);

        // Re-check pagination visibility on init and whenever Swiper crosses
        // into a different breakpoint (covers resize and orientation changes).
        const syncPagination = () => {
            updatePaginationVisibility(paginationEl, cards, realSlideCount, forceShowPagination, forceHidePagination);
        };

        // Re-check autoplay on init and on breakpoint/resize changes.
        const syncAutoplay = () => {
            if (!autoplayEnabled || !swiperInstance.autoplay) return;

            if (shouldAutoplay(window.innerWidth, realSlideCount)) {
                swiperInstance.autoplay.start();
            } else {
                swiperInstance.autoplay.stop();
            }
        };

        swiperInstance.on('breakpoint', syncPagination);
        swiperInstance.on('breakpoint', syncAutoplay);
        swiperInstance.on('resize', syncPagination);
        swiperInstance.on('resize', syncAutoplay);
        syncPagination();
        syncAutoplay();

        cards_swipers.push(swiperInstance);
    });
}

// Kept for other sections that may still rely on slide padding for loop mode.
export const padSlides = function(swiperEl, minSlides = 12) {
    const wrapper = swiperEl.querySelector(".swiper-wrapper");
    if (!wrapper) return;

    let slides = Array.from(wrapper.children).filter((n) =>
        n.classList.contains("swiper-slide")
    );

    if (slides.length >= minSlides) return;

    while (slides.length < minSlides) {
        slides.forEach((slide) => {
            const node = slide.cloneNode(true);
            node.querySelector('img')?.classList?.add('loaded');
            wrapper.appendChild(node)
        });
        slides = Array.from(wrapper.children).filter((n) =>
            n.classList.contains("swiper-slide")
        );
    }
}

document.addEventListener("DOMContentLoaded", () => {
    initialiseSwipers();
});